import type { SavedTargetingRules } from 'definitions';
import { flattenTargeting } from 'helpers/targeting-identity';

export const MAX_SAVED_TARGETING_NAME_LENGTH = 80;

type Named = { id: number; name: string; rules: SavedTargetingRules };

const plural = ({ count, word }: { count: number; word: string }) => `${count} ${word} rule${count === 1 ? '' : 's'}`;

/**
 * One line saying what a saved targeting holds: "Mobile · 1 URL rule · 1 activation rule".
 * Devices are named, the other groups counted, in the Target Area's order. No rules at all reads
 * as running on all, since targeting only ever narrows who sees an experiment.
 */
export function summarizeSavedRules(rules: SavedTargetingRules): string {
  const parts = [
    ...rules.device.map(({ device }) => String(device)),
    rules.url.length ? plural({ count: rules.url.length, word: 'URL' }) : '',
    rules.cookie.length ? plural({ count: rules.cookie.length, word: 'cookie' }) : '',
    rules.activation.length ? plural({ count: rules.activation.length, word: 'activation' }) : '',
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'No targeting (runs on all)';
}

/** Entries whose name or any condition label contains the query, ignoring case. */
export function filterSavedTargetings<T extends Named>(list: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...list];
  return list.filter(
    ({ name, rules }) =>
      name.toLowerCase().includes(needle) ||
      flattenTargeting(rules).some(({ label }) => label.toLowerCase().includes(needle)),
  );
}

type NameErrorParams = { name: string; list: readonly Named[]; exceptId?: number };

/**
 * Why a name cannot be used, or null. Mirrors the API (1 to 80 characters, unique on the website
 * ignoring case) so the dialog can say so inline before a request; the API still decides.
 */
export function savedTargetingNameError({ name, list, exceptId }: NameErrorParams): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_SAVED_TARGETING_NAME_LENGTH) return 'Keep the name under 80 characters.';
  const clash = list.some((entry) => entry.id !== exceptId && entry.name.toLowerCase() === trimmed.toLowerCase());
  return clash
    ? 'A saved targeting with this name already exists. Pick another name or switch to Update existing.'
    : null;
}

/** "Updated 2 Oct". */
export const formatUpdated = (timestamp: number): string =>
  `Updated ${new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;

/** A name cut to `max` characters with an ellipsis, for button labels. */
export const truncateName = (name: string, max = 32): string =>
  name.length > max ? `${name.slice(0, max - 1)}…` : name;
